/* ==========================================================================
   THE FLAIRE — Product page closer (v16)
   The answers open themselves as the customer scrolls; The Close reveals on
   entry; the add-to-bag buttons hand off to the real one. No dependencies.
   v16: every FAQ image is fetched the moment the section comes within a
   viewport of the screen, so an opening question never waits for its shot.
   ========================================================================== */
(function () {
  'use strict';

  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ *
   * Sticky-bar clearance
   * Product pages carry an app-injected sticky add-to-cart bar. Measure
   * whatever is actually pinned to the bottom of the viewport and hand its
   * height to the CSS as --fc-safe so nothing is ever hidden behind it.
   * ------------------------------------------------------------------ */
  function pinnedHeight(el, vw, vh) {
    var cs;
    try { cs = getComputedStyle(el); } catch (e) { return 0; }
    if (cs.position !== 'fixed' && cs.position !== 'sticky') return 0;
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return 0;
    var r = el.getBoundingClientRect();
    if (r.height < 24 || r.height > 300) return 0;
    if (r.width < vw * 0.55) return 0;
    if (r.bottom < vh - 12 || r.top > vh - 12) return 0;
    return r.height;
  }

  function measureSafe(root) {
    var vw = window.innerWidth, vh = window.innerHeight, h = 0;
    var seen = [], i, j, el;

    /* 1 · whatever is actually painted at the bottom edge of the screen.
       This is the only reliable way to find an app bar: it catches custom
       elements, late injections and shadow hosts that no selector knows. */
    var pts = [[vw * 0.5, vh - 3], [14, vh - 3], [vw - 14, vh - 3],
               [vw * 0.5, vh - 26], [vw * 0.5, vh - 52]];
    if (document.elementsFromPoint) {
      for (i = 0; i < pts.length; i++) {
        var stack;
        try { stack = document.elementsFromPoint(pts[i][0], pts[i][1]) || []; }
        catch (e) { stack = []; }
        for (j = 0; j < stack.length && j < 8; j++) seen.push(stack[j]);
      }
    }

    /* 2 · the usual suspects, by name */
    for (i = 0; i < document.body.children.length; i++) seen.push(document.body.children[i]);
    var hooks = document.querySelectorAll(
      '[class*="sticky" i],[id*="sticky" i],[class*="atc" i],[id*="atc" i],' +
      '[class*="add-to-cart" i],[class*="addtocart" i],[data-sticky]');
    for (i = 0; i < hooks.length; i++) seen.push(hooks[i]);

    for (i = 0; i < seen.length; i++) {
      el = seen[i];
      if (!el || el.nodeType !== 1) continue;
      if (el === document.body || el === document.documentElement) continue;
      if (el.closest && el.closest('[data-fc-closer]')) continue;
      /* the pinned thing may be an ancestor of what we hit */
      var node = el, up = 0;
      while (node && node.nodeType === 1 && up < 6) {
        if (node === document.body || node === document.documentElement) break;
        if (!(node.closest && node.closest('[data-fc-closer]'))) {
          var ph = pinnedHeight(node, vw, vh);
          if (ph > h) h = ph;
        }
        node = node.parentElement || (node.getRootNode && node.getRootNode().host) || null;
        up++;
      }
    }
    root.style.setProperty('--fc-safe', Math.round(h) + 'px');
  }

  function watchSafe(root) {
    var run = function () { measureSafe(root); };
    run();
    /* apps inject their bar late, and it can appear only once you scroll */
    var t0 = [400, 1200, 2600, 5000, 9000], k;
    for (k = 0; k < t0.length; k++) setTimeout(run, t0[k]);
    window.addEventListener('resize', run);
    window.addEventListener('orientationchange', function () { setTimeout(run, 300); });
    var t = null;
    window.addEventListener('scroll', function () {
      if (t) return;
      t = setTimeout(function () { t = null; run(); }, 250);
    }, { passive: true });
    if (window.MutationObserver) {
      var mo = new MutationObserver(function () {
        if (t) return;
        t = setTimeout(function () { t = null; run(); }, 300);
      });
      mo.observe(document.body, { childList: true, subtree: false });
    }
  }

  /* ------------------------------------------------------------------ *
   * Image preload
   * v16: the old version loaded each shot only when its question opened,
   * so the copy appeared a beat before the photograph. Now every data-src
   * image in the section is fetched as soon as the section comes within
   * ~1.2 viewports of the screen — by the time a question can open, its
   * shot is already in cache.
   * ------------------------------------------------------------------ */
  function loadImg(img) {
    if (!img) return;
    var ss = img.getAttribute('data-srcset');
    if (ss) img.setAttribute('srcset', ss);
    var src = img.getAttribute('data-src');
    if (src) img.setAttribute('src', src);
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  }

  function loadAllImgs(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('img[data-src]'), loadImg);
  }

  function primeImages(root) {
    if (root.dataset.fcPrimed === '1') return;
    root.dataset.fcPrimed = '1';
    if (!('IntersectionObserver' in window)) { loadAllImgs(root); return; }
    var io = new IntersectionObserver(function (e) {
      for (var i = 0; i < e.length; i++) {
        if (e[i].isIntersecting) { io.disconnect(); loadAllImgs(root); return; }
      }
    }, { rootMargin: '1200px 0px' });
    io.observe(root);
    /* fallback: any interaction with the section loads everything at once */
    var once = function () { io.disconnect(); loadAllImgs(root); };
    root.addEventListener('pointerdown', once, { once: true, passive: true });
    root.addEventListener('touchstart', once, { once: true, passive: true });
  }

  /* ------------------------------------------------------------------ *
   * The answers
   * One question is open at a time: whichever sits closest to the middle of
   * the screen. Scrolling therefore walks the customer through the objections
   * instead of handing them a wall of text. Tapping a question opens it too.
   * ------------------------------------------------------------------ */
  function initReel(root) {
    if (root.dataset.frReady === '1') return;
    root.dataset.frReady = '1';

    primeImages(root);

    var qs = Array.prototype.slice.call(root.querySelectorAll('[data-fc-q]'));
    if (!qs.length) return;
    var pinned = -1;   /* set when the customer taps, cleared on the next scroll */
    var open = -1;

    function load(el) { loadImg(el.querySelector('img[data-src]')); }

    function show(i) {
      if (i === open) return;
      open = i;
      for (var k = 0; k < qs.length; k++) {
        var on = k === i;
        qs[k].classList.toggle('is-open', on);
        var btn = qs[k].querySelector('.fq-qhead');
        if (btn) btn.setAttribute('aria-expanded', on ? 'true' : 'false');
        if (on) { load(qs[k]); load(qs[k + 1] || qs[k]); }
      }
    }

    /* the closing frame is not a question, so nothing opens it */
    var last = root.querySelector('.fq-last');

    if (reduce) {
      loadAllImgs(root);
      qs.forEach(function (q) { q.classList.add('is-open'); });
      return;
    }

    /* the band the open question is chosen from: a little above centre, which
       is where the eye already is on a phone */
    function pick() {
      var target = window.innerHeight * 0.42, best = -1, bestD = 1e9;
      for (var i = 0; i < qs.length; i++) {
        var r = qs[i].querySelector('.fq-qhead').getBoundingClientRect();
        var d = Math.abs(r.top + r.height / 2 - target);
        if (r.top > window.innerHeight || r.bottom < 0) continue;
        if (d < bestD) { bestD = d; best = i; }
      }
      if (best < 0) return;
      show(pinned >= 0 ? pinned : best);
    }

    var raf = null, running = false;
    function onScroll() {
      if (pinned >= 0) { pinned = -1; }
      if (!raf) raf = requestAnimationFrame(function () { raf = null; pick(); });
    }

    qs.forEach(function (q, i) {
      var btn = q.querySelector('.fq-qhead');
      if (!btn) return;
      btn.addEventListener('click', function () {
        pinned = (open === i) ? -1 : i;
        show(pinned >= 0 ? i : open);
        if (pinned >= 0) load(q);
      });
    });

    var io = new IntersectionObserver(function (e) {
      var vis = e[0].isIntersecting;
      if (vis && !running) {
        running = true;
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        if (last) load(last);
        pick();
      } else if (!vis && running) {
        running = false;
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    }, { rootMargin: '200px 0px' });
    io.observe(root);
    pick();
  }

  /* ------------------------- add to bag hand-off ------------------------- */
  var ATC = ['product-form button[name="add"]', 'form[action*="/cart/add"] button[name="add"]',
             '.product-form__submit', 'button[name="add"]'];
  function findAtc() {
    for (var i = 0; i < ATC.length; i++) {
      var els = document.querySelectorAll(ATC[i]);
      for (var j = 0; j < els.length; j++) {
        if (els[j].offsetParent !== null && !els[j].disabled) return els[j];
      }
      if (els.length) return els[0];
    }
    return null;
  }
  function onAtcClick(e) {
    e.preventDefault();
    var btn = findAtc();
    if (!btn) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    var box = btn.getBoundingClientRect();
    if (box.top > 40 && box.bottom < window.innerHeight - 20) { btn.click(); return; }
    btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(function () { var b = findAtc(); if (b && !b.disabled) b.click(); }, 700);
  }

  /* --------------------- The Close: reveal + icon start ------------------ */
  function initReveal(scope) {
    var els = Array.prototype.slice.call(scope.querySelectorAll('[data-fc-reveal]'));
    if (!els.length) return;
    if (reduce || !window.IntersectionObserver) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.2 });
    els.forEach(function (el, i) {
      if (el.dataset.fcRevealBound === '1') return;
      el.dataset.fcRevealBound = '1';
      el.style.setProperty('--i', i % 4);
      io.observe(el);
    });
  }

  function boot(scope) {
    var s = scope || document;
    Array.prototype.forEach.call(s.querySelectorAll('[data-fc-closer]'), watchSafe);
    initReveal(s);
    Array.prototype.forEach.call(s.querySelectorAll('[data-fc-reel]'), initReel);
    Array.prototype.forEach.call(s.querySelectorAll('[data-fc-atc]'), function (b) {
      if (b.dataset.fcAtcBound === '1') return;
      b.dataset.fcAtcBound = '1';
      b.addEventListener('click', onAtcClick);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else { boot(); }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  window.FlaireClose = { boot: boot };
})();